import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "playButton", "overlay"]
  static values = {
    videoId: { type: String, default: "professor_mode_demo" }
  }

  connect() {
    // Initialize tracking state
    this.hasStarted = false
    this.lastProgressPercentage = 0
    this.progressCheckpoints = [25, 50, 75]

    // Bind event handlers
    this.handlePlay = this.handlePlay.bind(this)
    this.handlePause = this.handlePause.bind(this)
    this.handleEnded = this.handleEnded.bind(this)
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this)

    // Add event listeners
    this.videoTarget.addEventListener('play', this.handlePlay)
    this.videoTarget.addEventListener('pause', this.handlePause)
    this.videoTarget.addEventListener('ended', this.handleEnded)
    this.videoTarget.addEventListener('timeupdate', this.handleTimeUpdate)

    // Ensure overlay is visible on load
    this.showOverlay()
  }

  disconnect() {
    // Clean up event listeners
    this.videoTarget.removeEventListener('play', this.handlePlay)
    this.videoTarget.removeEventListener('pause', this.handlePause)
    this.videoTarget.removeEventListener('ended', this.handleEnded)
    this.videoTarget.removeEventListener('timeupdate', this.handleTimeUpdate)
  }

  handlePlay() {
    if (!this.hasStarted) {
      this.trackVideoStarted()
      this.hasStarted = true
    } else {
      this.trackVideoResumed()
    }
  }

  handlePause() {
    // Only track pause if video hasn't ended (ended also triggers pause)
    if (!this.videoTarget.ended) {
      this.trackVideoPaused()
    }
  }

  handleEnded() {
    this.trackVideoCompleted()
    this.showPlayButton()
    this.showOverlay()
  }

  handleTimeUpdate() {
    const percentage = (this.videoTarget.currentTime / this.videoTarget.duration) * 100

    // Track progress at 25%, 50%, 75% milestones
    for (const checkpoint of this.progressCheckpoints) {
      if (percentage >= checkpoint && this.lastProgressPercentage < checkpoint) {
        this.trackVideoProgress()
        this.lastProgressPercentage = checkpoint
        break
      }
    }
  }

  togglePlay(event) {
    event.preventDefault()

    if (this.videoTarget.paused) {
      // Start playing and request fullscreen
      const playPromise = this.videoTarget.play()

      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.hidePlayButton()
          this.hideOverlay()
        }).catch(error => {
          console.error('Error playing video:', error)
        })
      } else {
        this.hidePlayButton()
        this.hideOverlay()
      }
    } else {
      this.videoTarget.pause()
      this.showPlayButton()
      this.showOverlay()
    }
  }

  hidePlayButton() {
    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.add('hidden')
    }
  }

  showPlayButton() {
    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.remove('hidden')
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden')
    }
  }

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
    }
  }

  trackVideoStarted() {
    this.sendEvent('DemoVideoStarted', {
      video_id: this.videoIdValue,
      duration_seconds: this.videoTarget.duration
    })
  }

  trackVideoPaused() {
    this.sendEvent('DemoVideoPaused', {
      video_id: this.videoIdValue,
      timestamp_seconds: this.videoTarget.currentTime,
      duration_seconds: this.videoTarget.duration,
      percentage_watched: (this.videoTarget.currentTime / this.videoTarget.duration) * 100
    })
  }

  trackVideoResumed() {
    this.sendEvent('DemoVideoResumed', {
      video_id: this.videoIdValue,
      timestamp_seconds: this.videoTarget.currentTime,
      duration_seconds: this.videoTarget.duration
    })
  }

  trackVideoCompleted() {
    this.sendEvent('DemoVideoCompleted', {
      video_id: this.videoIdValue,
      duration_seconds: this.videoTarget.duration
    })
  }

  trackVideoProgress() {
    this.sendEvent('DemoVideoProgress', {
      video_id: this.videoIdValue,
      timestamp_seconds: this.videoTarget.currentTime,
      duration_seconds: this.videoTarget.duration,
      percentage_watched: (this.videoTarget.currentTime / this.videoTarget.duration) * 100
    })
  }

  sendEvent(eventClass, payload) {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    if (!csrfToken) {
      console.warn('CSRF token not found, skipping analytics event')
      return
    }

    fetch('/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        event_class: eventClass,
        ...payload
      })
    }).catch(error => {
      console.error('Error tracking event:', error)
    })
  }
}
